/**
 * Design tokens for JavaScript consumers.
 *
 * Charting libraries (recharts) take raw colour strings rather than class
 * names, so they can't go through Tailwind. Rather than duplicating hex
 * values, this reads the same `:root` custom properties that the Tailwind
 * classes compile to — index.css stays the single source of truth.
 *
 * Fallbacks exist only for the window before stylesheets resolve (and for
 * tests running without a DOM); they mirror index.css and should be updated
 * alongside it if a token's default changes.
 */

const FALLBACK: Record<string, string> = {
  canvas: '10 12 16',
  surface: '18 21 28',
  'surface-raised': '21 25 35',
  'accent-400': '229 181 77',
  'accent-500': '212 175 55',
  'up-400': '52 211 153',
  'down-400': '248 113 113',
  'gold-400': '251 191 36',
  flat: '156 163 175',
  'chart-axis': '107 114 128',
  'chart-label': '156 163 175',
};

const cache = new Map<string, string>();

/** Resolve a token to an rgb()/rgba() string. `alpha` of 1 yields rgb(). */
export const token = (name: string, alpha = 1): string => {
  let channels = cache.get(name);

  if (channels === undefined) {
    const resolved =
      typeof window !== 'undefined'
        ? getComputedStyle(document.documentElement)
            .getPropertyValue(`--${name}`)
            .trim()
        : '';

    channels = resolved || FALLBACK[name] || '0 0 0';

    // Only cache once the stylesheet has actually answered; otherwise an
    // early call would pin the fallback for the rest of the session.
    if (resolved) cache.set(name, channels);
  }

  return alpha === 1 ? `rgb(${channels})` : `rgb(${channels} / ${alpha})`;
};

export type Direction = 'up' | 'down' | 'flat';

/**
 * The single definition of market direction. Zero is flat, not a gain —
 * every consumer (chips, chart series, sparklines) must agree, otherwise a
 * chart can render green above a neutral chip describing the same number.
 */
export const direction = (change: number): Direction =>
  change > 0 ? 'up' : change < 0 ? 'down' : 'flat';

/** Stroke/fill for a price series, matched to its PriceChange chip. */
export const seriesColor = (change: number, alpha = 1): string => {
  const d = direction(change);
  return token(d === 'flat' ? 'flat' : `${d}-400`, alpha);
};

/**
 * Where the baseline sits as a 0–1 fraction of a series' vertical range, for
 * splitting a gradient at the opening price. Above the baseline reads as
 * gain, below as loss — which is the entire point of a baseline chart, and
 * what a single flat fill throws away.
 */
export const baselineOffset = (values: number[], baseline: number): number => {
  if (values.length === 0) return 0;
  const max = Math.max(...values);
  const min = Math.min(...values);
  if (max <= baseline) return 0; // entirely at or below the open
  if (min >= baseline) return 1; // entirely at or above the open
  return (max - baseline) / (max - min);
};

/**
 * Categorical palette for user-assigned portfolio box colours. Ordered for
 * maximum adjacent-hue separation, not by value — these encode identity, not
 * magnitude or direction, so they deliberately avoid the up/down semantics.
 */
export const CATEGORICAL_SERIES: string[] = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#6366F1',
  '#14B8A6',
];
